/**
 * Admin-only: what each team position currently says, when it was last written,
 * and a way to rebuild it.
 *
 * Recompute already existed on the Opinions page, but the question that
 * prompts it — "is this position stale?" — isn't answerable there. A recompute
 * is scheduled in-process after every response change, so a restart between the
 * schedule and the fire drops it silently: the position simply stays old with
 * nothing to indicate it. Showing set-at next to updated-at makes that visible.
 */
import { useMemo } from "react";
import { Loader2, Pin, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useOpinions, useSynthesizeTopic } from "@/lib/useOpinions";
import { cn } from "@/lib/utils";
import type { TeamPosition } from "@/lib/types";

function when(value: string | null | undefined): string {
  if (!value) return "never";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "unknown";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function TopicPositions({ topicKey, positions }: { topicKey: string; positions: TeamPosition[] }) {
  const synthesize = useSynthesizeTopic();
  // Responses that landed after the prose was written — per position, since
  // decision points drift independently. The header shows the worst case only;
  // each row carries its own count, because "something here is stale" is not
  // actionable without knowing which.
  const worstDrift = positions.reduce((worst, p) => Math.max(worst, p.new_since_set), 0);

  return (
    <div className="flex flex-col gap-2 border-t border-bw-blue-muted/30 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="font-sans text-sm font-medium text-bw-ink">{positions[0].topic_title}</span>
          <span className="font-sans text-xs text-bw-ink-muted">
            {positions.length} position{positions.length === 1 ? "" : "s"}
            {worstDrift > 0 && (
              <span className="text-bw-navy">
                {" "}
                · up to {worstDrift} response{worstDrift === 1 ? "" : "s"} newer than the prose
              </span>
            )}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          disabled={synthesize.isPending}
          onClick={() => synthesize.mutate(topicKey)}
          title="Rebuild this topic's positions from the responses that currently count"
        >
          {synthesize.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <RefreshCw className="h-4 w-4" aria-hidden />
          )}
          {synthesize.isPending ? "Recomputing…" : "Recompute"}
        </Button>
      </div>

      {positions.map((position) => (
        <div key={position.opinion_id} className="flex flex-col gap-0.5 pl-3">
          <span className="flex items-center gap-1.5 font-sans text-xs text-bw-ink">
            {position.source === "curated" && <Pin className="h-3 w-3 text-bw-blue" aria-hidden />}
            {position.decision_point_title}
            <span className={cn("text-bw-ink-muted", position.new_since_set > 0 && "text-bw-navy")}>
              · written {when(position.position_set_at)}
              {position.respondent_count > 0 && (
                <>
                  {" "}
                  from {position.respondent_count} take{position.respondent_count === 1 ? "" : "s"}
                </>
              )}
              {position.new_since_set > 0 && (
                <>
                  {" "}
                  · {position.new_since_set} newer response
                  {position.new_since_set === 1 ? "" : "s"} since
                </>
              )}
            </span>
          </span>
          {position.decision && (
            <span className="line-clamp-2 font-sans text-xs text-bw-ink-muted">{position.decision}</span>
          )}
        </div>
      ))}

      {synthesize.isError && (
        <span className="font-sans text-xs text-bw-ink-muted">Recompute failed — try again shortly.</span>
      )}
    </div>
  );
}

export function AdminPositions() {
  const { data, isLoading, isError } = useOpinions();

  const byTopic = useMemo(() => {
    const grouped = new Map<string, TeamPosition[]>();
    for (const position of data ?? []) {
      const list = grouped.get(position.topic_key) ?? [];
      list.push(position);
      grouped.set(position.topic_key, list);
    }
    return [...grouped.entries()];
  }, [data]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Team positions</CardTitle>
        <p className="font-sans text-xs text-bw-ink-muted">
          When each position was last written, and how many responses have landed
          since. Recompute rebuilds a topic from the takes that currently count —
          excluded and discarded ones don't.
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading && <p className="font-sans text-sm text-bw-ink-muted">Loading positions…</p>}
        {isError && <p className="font-sans text-sm text-bw-ink-muted">Couldn&apos;t load positions.</p>}
        {!isLoading && !isError && byTopic.length === 0 && (
          <p className="font-sans text-sm text-bw-ink-muted">No positions synthesized yet.</p>
        )}
        {byTopic.map(([topicKey, positions]) => (
          <TopicPositions key={topicKey} topicKey={topicKey} positions={positions} />
        ))}
      </CardContent>
    </Card>
  );
}
